/**
 * Site-wide heartbeat (loaded from layout/theme.liquid on every page except the homepage, which has its own
 * route in assets/home-heartbeat.js). Same look as the homepage: one fast glowing pulse travels a faint line.
 *
 * Route, measured from the page itself:
 *   page title (h1, flashes) -> each visible section heading (h2 or a [data-ecg-stop] element, e.g. the Experiment
 *   Finder questions; data-ecg-stop="button" glows like the homepage button) -> every card grid, snaking row by
 *   row along the grid lines so each card lights up as the pulse passes -> loops back to the title.
 * Sequence markers (data-ecg-seq-node) beside a heading beat with it. Redrawn whenever the layout changes
 * (filters, resizing, content loading). Visitors who prefer reduced motion see the line without the pulse.
 */
const SPEED = 2200; // px per second
const PULSE = 160; // glowing segment length in px
const PAUSE = 450; // ms rest before the next loop
const MAX_STOPS = 60;
const SVG_NS = 'http://www.w3.org/2000/svg';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const main = document.getElementById('main-content');

if (main && !document.querySelector('[data-siri-orb]')) {
  init();
}

function init() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add('ecg-route');
  const base = document.createElementNS(SVG_NS, 'path');
  base.classList.add('ecg-route-base');
  const pulse = document.createElementNS(SVG_NS, 'path');
  pulse.classList.add('ecg-route-pulse');
  svg.append(base, pulse);
  document.body.append(svg);

  let route = null;

  function box(el) {
    const r = el.getBoundingClientRect();
    return {
      left: r.left + window.scrollX,
      top: r.top + window.scrollY,
      right: r.right + window.scrollX,
      bottom: r.bottom + window.scrollY,
      width: r.width,
      height: r.height,
    };
  }

  function visible(el) {
    if (el.closest('dialog, [hidden], [aria-hidden="true"], [inert], details:not([open]) > :not(summary)')) return false;
    const r = el.getBoundingClientRect();
    if (r.width < 12 || r.height < 6) return false; // also skips screen-reader-only headings
    const cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && Number(cs.opacity) > 0.05;
  }

  /** Page parts in reading order: the title, section headings and card grids. */
  function collect() {
    const title = [...main.querySelectorAll('h1')].find(visible);
    if (!title) return null;
    const parts = [];
    const candidates = main.querySelectorAll('h2, ul.grid, [data-ecg-stop]');
    for (const el of candidates) {
      if (!(title.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (!visible(el)) continue;
      if (el.tagName === 'H2' || el.hasAttribute('data-ecg-stop')) {
        if (el.closest('li, ul.grid')) continue; // headings inside cards belong to the card
        parts.push({ type: 'heading', el });
      } else {
        if (el.parentElement.closest('ul.grid')) continue; // nested grid
        const items = [...el.children].filter((li) => li.tagName === 'LI' && visible(li));
        if (items.length) parts.push({ type: 'grid', el, items });
      }
    }
    return { title, parts };
  }

  function build() {
    const found = collect();
    if (!found) return null;
    const { title, parts } = found;

    const pts = [];
    const stops = [];
    let dist = 0;
    const to = (x, y) => {
      const last = pts[pts.length - 1];
      if (last && Math.abs(last[0] - x) < 0.5 && Math.abs(last[1] - y) < 0.5) return;
      if (last) dist += Math.hypot(x - last[0], y - last[1]);
      pts.push([x, y]);
    };
    const here = () => pts[pts.length - 1];
    const stop = (...els) => {
      if (stops.length < MAX_STOPS) stops.push({ dist, els: els.filter(Boolean) });
    };
    // Move from the current spine to a new spine at `y` without crossing the element at `y`:
    // go down above the element, across, then down to it.
    // Move from the current spine to a new spine at `y` without cutting across photos: cross either just below
    // the previous stop or just above the target, whichever horizontal run clears every image on the page.
    const media = [...main.querySelectorAll('img, video, iframe')].filter(visible).map(box);
    const clear = (x1, x2, yy) => {
      const lo = Math.min(x1, x2);
      const hi = Math.max(x1, x2);
      return !media.some((m) => yy > m.top && yy < m.bottom && hi > m.left && lo < m.right);
    };
    let prevBottom = 0;
    const travelTo = (spine, y, topOfTarget) => {
      const [cx, cy] = here();
      if (Math.abs(cx - spine) > 1) {
        const below = Math.min(prevBottom + 12, topOfTarget - 16);
        const above = topOfTarget - 16;
        const crossY = below > cy && clear(cx, spine, below) ? below : above;
        to(cx, crossY);
        to(spine, crossY);
      }
      to(spine, y);
    };

    // Start: the page title flashes as the pulse leaves it.
    const t = box(title);
    const tSpine = Math.max(6, t.left - 20);
    const ty = t.top + Math.min(t.height / 2, 40);
    to(t.left - 4, ty);
    stop(title);
    to(tSpine, ty);
    prevBottom = t.bottom;

    for (const part of parts) {
      if (stops.length >= MAX_STOPS) break;
      if (part.type === 'heading') {
        const h = box(part.el);
        const spine = Math.max(6, h.left - 20);
        const hy = h.top + Math.min(h.height / 2, 28);
        travelTo(spine, hy, h.top);
        to(h.left - 4, hy);
        const marker = part.el.closest('section')?.querySelector('[data-ecg-seq-node]');
        stop(part.el, marker);
        prevBottom = h.bottom;
        to(spine, hy);
      } else {
        // Card grid: snake along the top edge of each row, left to right, then right to left, and so on.
        const g = box(part.el);
        const spine = Math.max(6, g.left - 20);
        const rows = [];
        for (const li of part.items) {
          const b = box(li);
          const row = rows.find((r) => Math.abs(r.top - b.top) < 4);
          if (row) row.cells.push({ li, b });
          else rows.push({ top: b.top, cells: [{ li, b }] });
        }
        rows.sort((a, b) => a.top - b.top);
        travelTo(spine, rows[0].top, g.top);
        rows.forEach((row, i) => {
          const cells = row.cells.sort((a, b) => a.b.left - b.b.left);
          const leftX = g.left;
          const rightX = cells[cells.length - 1].b.right;
          const forward = i % 2 === 0;
          const startX = forward ? leftX : rightX;
          if (i === 0) to(leftX, row.top);
          else {
            to(here()[0], row.top);
            to(startX, row.top);
          }
          const ordered = forward ? cells : [...cells].reverse();
          for (const c of ordered) {
            to(c.b.left + c.b.width / 2, row.top);
            stop(c.li);
          }
          to(forward ? rightX : leftX, row.top);
        });
        // Back to the spine along the grid's bottom edge.
        to(here()[0], g.bottom);
        to(spine, g.bottom);
        prevBottom = g.bottom;
      }
    }

    if (stops.length < 2) return null; // nothing to travel through
    stops.forEach((s) =>
      s.els.forEach((el) => {
        if (el.tagName === 'LI') el.classList.add('ecg-card');
        else if (el.dataset.ecgStop === 'button') el.classList.add('ecg-button');
        else el.classList.add('ecg-text');
      }),
    );
    const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    return { d, total: dist, stops };
  }

  function layout() {
    svg.setAttribute('width', document.documentElement.scrollWidth);
    svg.setAttribute('height', document.documentElement.scrollHeight);
    route = build();
    svg.style.display = route ? '' : 'none';
    if (!route) return;
    base.setAttribute('d', route.d);
    pulse.setAttribute('d', route.d);
    pulse.style.strokeDasharray = `${PULSE} ${route.total + PULSE}`;
  }

  function beat(el) {
    el.classList.remove('is-beating');
    void el.offsetWidth; // restart the animation
    el.classList.add('is-beating');
  }

  let start = null;
  let lastDist = -1;
  function frame(now) {
    if (reduceMotion.matches) {
      pulse.style.opacity = '0';
      return;
    }
    if (route) {
      if (start === null) start = now;
      const loop = (route.total / SPEED) * 1000 + PAUSE;
      const elapsed = (now - start) % loop;
      const head = (elapsed / 1000) * SPEED;
      if (head < lastDist) lastDist = -1; // new loop
      for (const s of route.stops) {
        if (s.dist > lastDist && s.dist <= head) s.els.forEach(beat);
      }
      lastDist = head;
      pulse.style.opacity = head <= route.total + PULSE ? '1' : '0';
      pulse.style.strokeDashoffset = `${-(Math.min(head, route.total + PULSE) - PULSE)}`;
    }
    requestAnimationFrame(frame);
  }

  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      layout();
    });
  };
  new ResizeObserver(schedule).observe(document.body);
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  document.fonts?.ready.then(schedule);

  layout();
  requestAnimationFrame(frame);
}
